import React, { useEffect, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import ToggleSetting from "../../components/ui/ToggleSetting";
import { Button } from "../../components/ui/Button";
import { Bell } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import { useAuth } from "../../auth/AuthContext";
import {
  createOrUpdateNotificationSetting,
  getNotificationSetting,
} from "../../services/settings";
import GlobalLoader from "../../components/ui/GlobalLoader";

const toBool = (raw: unknown, fallback = false): boolean => {
  if (raw === undefined || raw === null) return fallback;
  return raw === true || raw === 1 || raw === "1";
};

const NotificationSettings: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(true);
  const [confirmSms, setConfirmSms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const companyId =
    user?.company?.id ?? user?.company_id ?? user?.company?.company_id;

  useEffect(() => {
    let mounted = true;

    const loadSetting = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await getNotificationSetting(companyId);
        if (!mounted) return;

        if (response) {
          setSmsEnabled(toBool(response.is_sms_enable, false));
          setConfirmEmail(toBool(response.confirm_on_create_email, true));
          setConfirmSms(toBool(response.confirm_on_create_sms, false));
        } else {
          setSmsEnabled(false);
          setConfirmEmail(true);
          setConfirmSms(false);
        }
      } catch (error: any) {
        if (!mounted) return;
        toast.error(
          error?.message || "Failed to load notification setting."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void loadSetting();

    return () => {
      mounted = false;
    };
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId) {
      toast.error("Company not found for this account.");
      return;
    }

    setSaving(true);
    try {
      const response = await createOrUpdateNotificationSetting(companyId, {
        is_sms_enable: smsEnabled,
        confirm_on_create_email: confirmEmail,
        confirm_on_create_sms: confirmSms,
      });
      toast.success(response?.message || "Notification setting updated.");
    } catch (error: any) {
      toast.error(error?.message || "Failed to save notification setting.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading notification settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Notification Settings"
        description="Manage booking confirmations and SMS alerts."
        variant="card"
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Bell size={18} className="text-white" />
            Booking Notifications
          </h3>
        </div>

        <div className="p-5 flex-1 space-y-5">
          <ToggleSetting
            label="Enable SMS notifications"
            description="Master switch for SMS (reminders, consultation forms, confirmations)."
            checked={smsEnabled}
            onChange={setSmsEnabled}
            showBackground
          />

          <ToggleSetting
            label="Email confirmation when booking is created"
            description="Send a confirmation email when staff create a booking in Glossa."
            checked={confirmEmail}
            onChange={setConfirmEmail}
            showBackground
          />

          <ToggleSetting
            label="SMS confirmation when booking is created"
            description="Also requires SMS notifications to be enabled above."
            checked={confirmSms}
            onChange={setConfirmSms}
            showBackground
          />

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || !companyId}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
